import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Youtube } from 'lucide-react';
import AdBlock from './AdBlock';
import { useAuth } from '../context/AuthContext';
import { getSalesPhones } from '../lib/salesPhones';

export const Footer = () => {
  const { branding } = useAuth();
  const salesPhones = getSalesPhones(branding).filter(p => p !== branding?.contact_phone);
  const platformName = branding?.platform_name || 'WorksRun';

  return (
    <>
    <AdBlock position="footer" showEmpty />
    <footer className="bg-[#003366] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {branding?.logo_url && (
                <img
                  src={
                    branding.logo_url.startsWith('http')
                      ? branding.logo_url
                      : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
                  }
                  alt={platformName}
                  className="h-10 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              {!branding?.logo_url && (
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="font-bold text-xl">{platformName}</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              AI-powered job matching platform connecting talented professionals with top employers across the UK.
            </p>
            <div className="flex items-center gap-3">
              {branding?.social_linkedin && (
                <a href={branding.social_linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {!branding?.social_linkedin && (
                <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {branding?.social_twitter && (
                <a href={branding.social_twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {!branding?.social_twitter && (
                <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {branding?.social_facebook && (
                <a href={branding.social_facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {!branding?.social_facebook && (
                <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {branding?.social_youtube && (
                <a href={branding.social_youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Youtube className="w-4 h-4" />
                </a>
              )}
              {!branding?.social_youtube && (
                <a href="#" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Youtube className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Job Seekers */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Job Seekers</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/jobs" className="text-white/70 hover:text-white text-sm transition-colors">
                  Search Jobs
                </Link>
              </li>
              <li>
                <Link to="/cv-builder" className="text-white/70 hover:text-white text-sm transition-colors">
                  CV Builder
                </Link>
              </li>
              <li>
                <Link to="/career-advice" className="text-white/70 hover:text-white text-sm transition-colors">
                  Career Advice
                </Link>
              </li>
              <li>
                <Link to="/salary-checker" className="text-white/70 hover:text-white text-sm transition-colors">
                  Salary Checker
                </Link>
              </li>
              <li>
                <Link to="/alerts" className="text-white/70 hover:text-white text-sm transition-colors">
                  Job Alerts
                </Link>
              </li>
            </ul>
          </div>

          {/* Employers */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Employers</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/employer/post-job" className="text-white/70 hover:text-white text-sm transition-colors">
                  Post a Job
                </Link>
              </li>
              <li>
                <Link to="/employer/dashboard" className="text-white/70 hover:text-white text-sm transition-colors">
                  Employer Dashboard
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-white/70 hover:text-white text-sm transition-colors">
                  Pricing Plans
                </Link>
              </li>
              <li>
                <Link to="/recruiter" className="text-white/70 hover:text-white text-sm transition-colors">
                  Hiring Resources
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-white/70 hover:text-white text-sm transition-colors">
                  Events
                </Link>
              </li>
            </ul>
          </div>

          {/* Education */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Education</h3>
            <ul className="space-y-3">
              <li><Link to="/courses" className="text-white/70 hover:text-white text-sm transition-colors">Browse Courses</Link></li>
              <li><Link to="/universities" className="text-white/70 hover:text-white text-sm transition-colors">Universities</Link></li>
              <li><Link to="/student-services" className="text-white/70 hover:text-white text-sm transition-colors">Student Services</Link></li>
              <li><Link to="/events" className="text-white/70 hover:text-white text-sm transition-colors">Events & Open Days</Link></li>
              <li><Link to="/courses/post" className="text-white/70 hover:text-white text-sm transition-colors">Post a Course</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              {(branding?.contact_email) && (
                <li className="flex items-center gap-2 text-white/70 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{branding.contact_email}</span>
                </li>
              )}
              {!branding?.contact_email && (
                <li className="flex items-center gap-2 text-white/70 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>support@worksrun.com</span>
                </li>
              )}
              {branding?.contact_phone && (
                <li className="flex items-center gap-2 text-white/70 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{branding.contact_phone}</span>
                </li>
              )}
              {salesPhones.map(ph => (
                <li key={ph} className="flex items-center gap-2 text-white/70 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>Sales: {ph}</span>
                </li>
              ))}
              {branding?.address && (
                <li className="flex items-start gap-2 text-white/70 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span style={{ whiteSpace: 'pre-line' }}>{branding.address}</span>
                </li>
              )}
              {!branding?.address && (
                <li className="flex items-start gap-2 text-white/70 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>123 Business Street<br />London, EC2A 4NE</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} {platformName}. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/50 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="text-white/50 hover:text-white text-sm transition-colors">
              Cookie Policy
            </Link>
            <Link to="/help" className="text-white/50 hover:text-white text-sm transition-colors">
              Help / Manual
            </Link>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
